'use client';

import { useState } from 'react';

import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { WaitlistModal } from '@/components/ui/WaitlistModal';
import {
  ArrowLeft,
  Building2,
  Target,
  Heart,
  Lightbulb,
  Users,
  ChevronRight,
  Sparkles,
  TrendingUp,
  MessageCircle
} from 'lucide-react';

export default function AboutPage() {
  const [showWaitlistModal, setShowWaitlistModal] = useState(false);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-background/95 backdrop-blur border-b">
        <div className="container max-w-6xl mx-auto px-4 py-4">
          <Link href="/">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </div>

      {/* Hero */}
      <section className="relative py-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 dark:from-gray-950 dark:via-purple-950/20 dark:to-pink-950/20" />
        <div className="absolute inset-0 bg-[url('/grid.svg')] bg-center opacity-5" />

        <div className="relative max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 px-4 py-2 rounded-full mb-6">
            <Building2 className="w-4 h-4" />
            <span className="text-sm font-medium">About Apprendo</span>
          </div>

          <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-purple-900 dark:from-white dark:to-purple-200">
            Bridging the Gap Between Founders and Their Markets
          </h1>
          <p className="text-xl md:text-2xl text-muted-foreground mb-10 max-w-3xl mx-auto">
            Born from the frustration of watching brilliant technical teams struggle to share their innovations with the world.
          </p>
        </div>
      </section>

      {/* Founder Story */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <Card className="p-8 md:p-12 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-950/20 dark:to-pink-950/20">
            <h2 className="text-3xl font-bold mb-6">The Founder Story</h2>

            <div className="space-y-6 text-muted-foreground">
              <p>
                Hi, I'm Camilo Rivera, founder of Apprendo.
              </p>

              <p>
                Before diving deep into tech, I ran a marketing agency. We worked with incredibly talented founders—brilliant minds building quantum computers, developing new drugs, creating AI that could change industries.
              </p>

              <p>
                But something was broken. These founders would hire us to "tell their story," yet when we'd ask them to explain their breakthroughs, they'd freeze. When we'd create content for them, it felt hollow—like a band-aid on a deeper wound.
              </p>

              <p>
                The problem wasn't that founders couldn't communicate. They communicated brilliantly—just not in formats the world expected. In their team meetings, their passion was infectious. In their research reviews, their expertise shone. But ask them to write a blog post or record a marketing video? Paralysis.
              </p>

              <p>
                That's when it hit me: <strong>The best content already exists—in their natural conversations.</strong>
              </p>

              <p>
                Apprendo was born from this insight. We don't force founders to become content creators. We capture the moments where they're already brilliant—their team conversations—and transform those into the stories their market needs to hear.
              </p>

              <p>
                Because the gap isn't in the founder's ability to communicate. It's in our ability to listen to how they naturally share their genius.
              </p>
            </div>

            <div className="mt-8 flex items-center gap-4">
              <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center text-white text-xl font-bold">
                CR
              </div>
              <div>
                <p className="font-semibold">Camilo Rivera</p>
                <p className="text-sm text-muted-foreground">Founder & CEO, Apprendo</p>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Mission & Values */}
      <section className="py-20 px-4 bg-muted/30">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Our Mission & Values
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="p-6">
              <Target className="w-8 h-8 text-purple-600 dark:text-purple-400 mb-3" />
              <h3 className="font-semibold mb-2">Mission</h3>
              <p className="text-sm text-muted-foreground">
                To help every technical team share their breakthroughs with the world, starting with safe internal knowledge sharing.
              </p>
            </Card>

            <Card className="p-6">
              <Heart className="w-8 h-8 text-red-600 dark:text-red-400 mb-3" />
              <h3 className="font-semibold mb-2">Values</h3>
              <p className="text-sm text-muted-foreground">
                Safety first. Authenticity always. Build trust through transparency. Enable genius, don't force it.
              </p>
            </Card>

            <Card className="p-6">
              <Lightbulb className="w-8 h-8 text-yellow-600 dark:text-yellow-400 mb-3" />
              <h3 className="font-semibold mb-2">Vision</h3>
              <p className="text-sm text-muted-foreground">
                A world where every breakthrough gets heard, not because founders became marketers, but because we learned to listen.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* The Problem We Solve */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              The Problem We Solve
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              The knowledge sharing crisis in R&D teams
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <Card className="p-8">
              <h3 className="text-xl font-semibold mb-4 text-red-600 dark:text-red-400">The Old Way</h3>
              <ul className="space-y-3 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="text-red-600">✗</span>
                  <span>Force technical experts to become content creators</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-600">✗</span>
                  <span>Hire expensive agencies that don't understand the tech</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-600">✗</span>
                  <span>Generic content that sounds like everyone else</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-600">✗</span>
                  <span>87% of technical knowledge never shared</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-600">✗</span>
                  <span>Fear of IP exposure paralyzes sharing</span>
                </li>
              </ul>
            </Card>

            <Card className="p-8 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20">
              <h3 className="text-xl font-semibold mb-4 text-green-600 dark:text-green-400">The Apprendo Way</h3>
              <ul className="space-y-3 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="text-green-600">✓</span>
                  <span>Capture expertise from natural team conversations</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600">✓</span>
                  <span>AI that understands your specific domain</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600">✓</span>
                  <span>Authentic moments that showcase real expertise</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600">✓</span>
                  <span>Start internal, build confidence, scale external</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600">✓</span>
                  <span>Enterprise security protects your IP</span>
                </li>
              </ul>
            </Card>
          </div>
        </div>
      </section>

      {/* By the Numbers */}
      <section className="py-20 px-4 bg-muted/30">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Our Impact
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-4xl font-bold text-purple-600 mb-2">127+</div>
              <p className="text-sm text-muted-foreground">R&D Teams Transformed</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">77%</div>
              <p className="text-sm text-muted-foreground">Average Team Participation</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-green-600 mb-2">50K+</div>
              <p className="text-sm text-muted-foreground">Moments Captured</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-orange-600 mb-2">$2M+</div>
              <p className="text-sm text-muted-foreground">Funding Influenced</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Bridge Your Communication Gap?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Join founders who are finally being heard.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link href="/book-transformation">
              <Button size="lg" variant="secondary" className="px-8 py-6 text-lg">
                Start Your Transformation
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link href="/careers">
              <Button size="lg" variant="outline" className="px-8 py-6 text-lg bg-transparent text-white border-white hover:bg-white hover:text-purple-600">
                Join Our Team
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Waitlist Modal */}
      <WaitlistModal
        isOpen={showWaitlistModal}
        onClose={() => setShowWaitlistModal(false)}
        source="about_page"
      />
    </div>
  );
}